// Mock data adapted from pharmatrace-hub for the Pharmacy sub-views.
export type LotStatut = "actif" | "rappelé" | "expiré" | "destruction";

export interface Medicament {
  cip: string;
  gtin: string;
  dci: string;
  nomCommercial: string;
  fabricant: string;
  forme: string;
  dosage: string;
  prixPublic: number;
  liste: "I" | "II" | "Stupéfiant" | "Libre";
}

export interface Lot {
  id: string;
  numeroLot: string;
  cip: string;
  fabrication: string;
  expiration: string;
  quantiteInitiale: number;
  quantiteRestante: number;
  fabricant: string;
  statut: LotStatut;
}

export type MouvementType =
  | "fabrication" | "importation" | "reception" | "transfert"
  | "vente" | "retour" | "rappel" | "destruction";

export interface Mouvement {
  id: string;
  timestamp: string;
  lotId: string;
  type: MouvementType;
  source: string;
  destination: string;
  quantite: number;
  ville?: string;
}

export interface AlerteStock {
  cip: string;
  nom: string;
  stockActuel: number;
  stockSecurite: number;
  consommationMensuelle: number;
  joursCouverture: number;
  region: string;
}

export interface Dispensation {
  id: string;
  date: string;
  patient: string;
  patientId: string;
  medicament: string;
  cip: string;
  lotId: string;
  quantite: number;
  prescripteur: string;
  type: "ordonnance" | "libre" | "stupéfiant";
  montant: number;
  rembCNSS: number;
}

export const medicaments: Medicament[] = [
  { cip: "3400930000017", gtin: "06111000000017", dci: "Paracétamol", nomCommercial: "Doliprane", fabricant: "Sanofi Maroc", forme: "Comprimé", dosage: "500 mg", prixPublic: 12.5, liste: "Libre" },
  { cip: "3400930000024", gtin: "06111000000024", dci: "Amoxicilline", nomCommercial: "Clamoxyl", fabricant: "GSK Maroc", forme: "Gélule", dosage: "500 mg", prixPublic: 34.8, liste: "I" },
  { cip: "3400930000031", gtin: "06111000000031", dci: "Salbutamol", nomCommercial: "Ventoline", fabricant: "GSK Maroc", forme: "Aérosol", dosage: "100 µg", prixPublic: 28.6, liste: "II" },
  { cip: "3400930000048", gtin: "06111000000048", dci: "Metformine", nomCommercial: "Glucophage", fabricant: "Merck Maroc", forme: "Comprimé", dosage: "850 mg", prixPublic: 22.4, liste: "II" },
  { cip: "3400930000086", gtin: "06111000000086", dci: "Insuline Glargine", nomCommercial: "Lantus", fabricant: "Sanofi Maroc", forme: "Stylo", dosage: "100 UI/ml", prixPublic: 312, liste: "I" },
  { cip: "3400930000116", gtin: "06111000000116", dci: "Tramadol", nomCommercial: "Topalgic", fabricant: "Sanofi Maroc", forme: "Gélule", dosage: "50 mg", prixPublic: 56.8, liste: "I" },
  { cip: "3400930000123", gtin: "06111000000123", dci: "Morphine", nomCommercial: "Skenan LP", fabricant: "Sanofi Maroc", forme: "Gélule LP", dosage: "30 mg", prixPublic: 89.4, liste: "Stupéfiant" },
];

const lotSeed: Array<[string, string, string, string, number, number, LotStatut]> = [
  ["3400930000017", "PARA240601", "2024-06-01", "2027-06-01", 120000, 42300, "actif"],
  ["3400930000024", "AMOX240115", "2024-01-15", "2026-01-15", 60000, 15800, "actif"],
  ["3400930000031", "VENT240420", "2024-04-20", "2026-04-20", 35000, 12400, "actif"],
  ["3400930000031", "VENT240210", "2024-02-10", "2026-02-10", 30000, 240, "rappelé"],
  ["3400930000048", "GLUC240502", "2024-05-02", "2027-05-02", 80000, 56200, "actif"],
  ["3400930000086", "LANT240115", "2024-01-15", "2025-07-15", 18000, 3100, "actif"],
  ["3400930000116", "TOPA240301", "2024-03-01", "2027-03-01", 40000, 18700, "actif"],
  ["3400930000123", "SKEN240122", "2024-01-22", "2027-01-22", 12000, 4300, "actif"],
];

export const lots: Lot[] = lotSeed.map(([cip, num, fab, exp, qi, qr, statut], i) => {
  const med = medicaments.find(m => m.cip === cip)!;
  return {
    id: `LOT-${String(i + 1).padStart(4, "0")}`,
    cip, numeroLot: num, fabrication: fab, expiration: exp,
    quantiteInitiale: qi, quantiteRestante: qr,
    fabricant: med.fabricant, statut,
  };
});

export const alertesStock: AlerteStock[] = [
  { cip: "3400930000086", nom: "Lantus 100 UI/ml", stockActuel: 4300, stockSecurite: 12000, consommationMensuelle: 6500, joursCouverture: 20, region: "Casablanca" },
  { cip: "3400930000031", nom: "Ventoline 100 µg", stockActuel: 12640, stockSecurite: 18000, consommationMensuelle: 9200, joursCouverture: 41, region: "Rabat" },
  { cip: "3400930000123", nom: "Skenan LP 30 mg", stockActuel: 4300, stockSecurite: 6000, consommationMensuelle: 2100, joursCouverture: 61, region: "National" },
  { cip: "3400930000024", nom: "Clamoxyl 500 mg", stockActuel: 15800, stockSecurite: 25000, consommationMensuelle: 14000, joursCouverture: 33, region: "Marrakech" },
];

export const mouvements: Mouvement[] = [
  { id: "M1", timestamp: "2024-06-01T08:00:00Z", lotId: "LOT-0001", type: "fabrication", source: "Sanofi Maroc — Site Casablanca", destination: "Entrepôt central", quantite: 120000, ville: "Casablanca" },
  { id: "M2", timestamp: "2024-06-05T10:30:00Z", lotId: "LOT-0001", type: "transfert", source: "Entrepôt central", destination: "Grossiste Cooper Pharma", quantite: 60000, ville: "Casablanca" },
  { id: "M3", timestamp: "2024-06-08T14:00:00Z", lotId: "LOT-0001", type: "transfert", source: "Grossiste Cooper Pharma", destination: "Dépôt Rabat", quantite: 18000, ville: "Rabat" },
  { id: "M4", timestamp: "2024-06-12T09:15:00Z", lotId: "LOT-0001", type: "reception", source: "Dépôt Rabat", destination: "Pharmacie hospitalière", quantite: 240, ville: "Rabat" },
  { id: "M5", timestamp: "2024-06-15T11:45:00Z", lotId: "LOT-0001", type: "vente", source: "Pharmacie hospitalière", destination: "Patient #P-29481", quantite: 1, ville: "Rabat" },
  { id: "M6", timestamp: "2024-02-10T07:00:00Z", lotId: "LOT-0004", type: "fabrication", source: "GSK Maroc", destination: "Entrepôt GSK", quantite: 30000, ville: "Casablanca" },
  { id: "M7", timestamp: "2024-05-22T13:00:00Z", lotId: "LOT-0004", type: "rappel", source: "AMMPS", destination: "Tous distributeurs", quantite: 29760, ville: "National" },
];

export const dispensations: Dispensation[] = [
  { id: "D-10421", date: "2024-06-22T09:14:00Z", patient: "James Miamai", patientId: "P-29481", medicament: "Doliprane 500 mg", cip: "3400930000017", lotId: "LOT-0001", quantite: 2, prescripteur: "Dr. Smith", type: "libre", montant: 25.0, rembCNSS: 0 },
  { id: "D-10422", date: "2024-06-22T10:02:00Z", patient: "Aïcha Bennani", patientId: "P-30205", medicament: "Clamoxyl 500 mg", cip: "3400930000024", lotId: "LOT-0002", quantite: 1, prescripteur: "Dr. Dubois", type: "ordonnance", montant: 34.8, rembCNSS: 24.36 },
  { id: "D-10423", date: "2024-06-22T10:48:00Z", patient: "Karim Ouahbi", patientId: "P-30112", medicament: "Ventoline 100 µg", cip: "3400930000031", lotId: "LOT-0003", quantite: 1, prescripteur: "Dr. El Idrissi", type: "ordonnance", montant: 28.6, rembCNSS: 20.02 },
  { id: "D-10424", date: "2024-06-22T11:30:00Z", patient: "Fatima Zahra", patientId: "P-30410", medicament: "Glucophage 850 mg", cip: "3400930000048", lotId: "LOT-0005", quantite: 1, prescripteur: "Dr. Smith", type: "ordonnance", montant: 22.4, rembCNSS: 15.68 },
  { id: "D-10425", date: "2024-06-22T12:15:00Z", patient: "Hassan Alami", patientId: "P-30599", medicament: "Lantus 100 UI/ml", cip: "3400930000086", lotId: "LOT-0006", quantite: 1, prescripteur: "Dr. Dubois", type: "ordonnance", montant: 312, rembCNSS: 280.8 },
  { id: "D-10426", date: "2024-06-22T13:40:00Z", patient: "Yasmine Idrissi", patientId: "P-30701", medicament: "Topalgic 50 mg", cip: "3400930000116", lotId: "LOT-0007", quantite: 1, prescripteur: "Dr. El Idrissi", type: "ordonnance", montant: 56.8, rembCNSS: 39.76 },
  { id: "D-10427", date: "2024-06-22T14:55:00Z", patient: "Mohamed Saidi", patientId: "P-30812", medicament: "Skenan LP 30 mg", cip: "3400930000123", lotId: "LOT-0008", quantite: 1, prescripteur: "Dr. Smith", type: "stupéfiant", montant: 89.4, rembCNSS: 89.4 },
];
